import axios from './axios';
import { getEssentialCodeList, getCompanyAssetTypes } from './api';

/**
 * 코드 목록 관리 유틸리티
 * 캐시 기능을 포함한 코드 목록 조회
 */

const CACHE_DURATION = 1 * 60 * 1000; // 1분 (60초)
const CACHE_KEY = 'code_list';
const CACHE_TIMESTAMP_KEY = 'code_list_updated';

// 진행 중인 요청을 추적하기 위한 변수
let pendingRequest = null;

/**
 * 코드 목록 조회
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {Promise<Array>} 코드 목록
 */
export const getCodeList = async (forceRefresh = false) => {
    const lastUpdated = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    const now = Date.now();
    
    // 캐시가 유효하고 강제 새로고침이 아닌 경우
    if (!forceRefresh && lastUpdated && (now - parseInt(lastUpdated)) < CACHE_DURATION) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch (e) {
                console.error('Failed to parse cached code list:', e);
            }
        }
    }
    
    // 이미 진행 중인 요청이 있으면 그 요청을 기다림
    if (pendingRequest && !forceRefresh) {
        return pendingRequest;
    }
    
    // 새로운 데이터 조회
    pendingRequest = (async () => {
        try {
            const response = await axios.get(getEssentialCodeList());
            const records = response.data.map(record => ({
                type: record.code_type,
                text: record.code_nm,
                value: record.code_cd,
                val_01: record.val_01,
                val_02: record.val_02,
                val_03: record.val_03,
                val_04: record.val_04,
                val_05: record.val_05
            }));
            
            // com_cd는 서버 정렬 순서 유지, 나머지는 텍스트 기준 정렬
            const comCdRecords = records.filter(r => r.type === 'com_cd');
            const otherRecords = records.filter(r => r.type !== 'com_cd');
            otherRecords.sort((a, b) => a.text.localeCompare(b.text));
            
            // result_cd 타입에만 '전체' 옵션 추가
            const dataList = [{ type: 'result_cd', text: '전체', value: '' }, ...comCdRecords, ...otherRecords];
            
            // 캐시 저장
            localStorage.setItem(CACHE_KEY, JSON.stringify(dataList));
            localStorage.setItem(CACHE_TIMESTAMP_KEY, now.toString());
            
            // 캐시 업데이트 이벤트 발생
            window.dispatchEvent(new Event('codeListUpdated'));
            
            pendingRequest = null; // 요청 완료
            return dataList;
        } catch (error) {
            console.error('Failed to fetch code list:', error);
            pendingRequest = null; // 오류 시에도 초기화
            
            // 오류 시 캐시된 데이터 반환 (있는 경우)
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                try {
                    return JSON.parse(cached);
                } catch (e) {
                    return [];
                }
            }
            
            return [];
        }
    })();
    
    return pendingRequest;
};

/**
 * 특정 타입의 코드 목록 조회
 * @param {string} codeType - 코드 타입
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {Promise<Array>} 필터링된 코드 목록
 */
export const getCodeListByType = async (codeType, forceRefresh = false) => {
    const allCodes = await getCodeList(forceRefresh);
    let filteredCodes = allCodes.filter(item => item.type === codeType);
    
    // result_cd와 com_cd를 제외한 타입에는 '전체' 옵션 추가
    if (codeType !== "result_cd" && codeType !== "com_cd") {
        filteredCodes.unshift({ type: codeType, text: "전체", value: "" });
    }
    
    return filteredCodes;
};

/**
 * 캐시 유효 시간 확인
 * @returns {boolean} 캐시가 유효한지 여부
 */
export const isCacheValid = () => {
    const lastUpdated = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!lastUpdated) return false;
    
    const now = Date.now();
    return (now - parseInt(lastUpdated)) < CACHE_DURATION;
};

/**
 * 캐시 초기화
 */
export const clearCodeCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
};

// 회사별 자산구분 캐시 관리
const COMPANY_ASSET_CACHE_DURATION = 5 * 60 * 1000; // 5분
const COMPANY_ASSET_CACHE_KEY_PREFIX = 'company_asset_types_';
const COMPANY_ASSET_CACHE_TIMESTAMP_PREFIX = 'company_asset_types_updated_';

// 진행 중인 회사별 자산구분 요청을 추적
let pendingCompanyAssetRequests = new Map();

/**
 * 회사별 사용중인 자산구분 조회 (캐시 포함)
 * @param {string} companyCode - 회사 코드
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {Promise<Array>} 회사별 자산구분 목록
 */
export const getCompanyAssetTypeList = async (companyCode, forceRefresh = false) => {
    if (!companyCode) return [];

    const cacheKey = COMPANY_ASSET_CACHE_KEY_PREFIX + companyCode;
    const timestampKey = COMPANY_ASSET_CACHE_TIMESTAMP_PREFIX + companyCode;
    const lastUpdated = localStorage.getItem(timestampKey);
    const now = Date.now();

    // 캐시가 유효하고 강제 새로고침이 아닌 경우
    if (!forceRefresh && lastUpdated && (now - parseInt(lastUpdated)) < COMPANY_ASSET_CACHE_DURATION) {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch (e) {
                console.error('Failed to parse cached company asset types:', e);
            }
        }
    }

    // 이미 진행 중인 요청이 있으면 그 요청을 기다림
    if (pendingCompanyAssetRequests.has(companyCode) && !forceRefresh) {
        return pendingCompanyAssetRequests.get(companyCode);
    }

    // 새로운 데이터 조회
    const requestPromise = (async () => {
        try {
            const response = await axios.get(getCompanyAssetTypes(companyCode));
            // 자산 목록에서 asset_type만 추출하고 중복 제거
            const assetTypes = [...new Set(
                response.data.results
                    ? response.data.results.map(item => item.asset_type).filter(type => type)
                    : response.data.map(item => item.asset_type).filter(type => type)
            )];

            // 캐시 저장
            localStorage.setItem(cacheKey, JSON.stringify(assetTypes));
            localStorage.setItem(timestampKey, now.toString());

            pendingCompanyAssetRequests.delete(companyCode);
            return assetTypes;
        } catch (error) {
            console.error('Failed to fetch company asset types:', error);
            pendingCompanyAssetRequests.delete(companyCode);

            // 오류 시 캐시된 데이터 반환 (있는 경우)
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                try {
                    return JSON.parse(cached);
                } catch (e) {
                    return [];
                }
            }

            return [];
        }
    })();

    pendingCompanyAssetRequests.set(companyCode, requestPromise);
    return requestPromise;
};

/**
 * 회사별 자산구분 캐시 초기화
 * @param {string} companyCode - 특정 회사코드 (없으면 전체 초기화)
 */
export const clearCompanyAssetCache = (companyCode = null) => {
    if (companyCode) {
        localStorage.removeItem(COMPANY_ASSET_CACHE_KEY_PREFIX + companyCode);
        localStorage.removeItem(COMPANY_ASSET_CACHE_TIMESTAMP_PREFIX + companyCode);
    } else {
        // 전체 회사별 자산구분 캐시 초기화
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(COMPANY_ASSET_CACHE_KEY_PREFIX) ||
                key.startsWith(COMPANY_ASSET_CACHE_TIMESTAMP_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    }
};